import React, { useState, useEffect, useContext, useRef } from 'react';
import Footer from '../components/footer';
import { EnvelopeIcon, BellIcon, PencilSquareIcon, ChevronDownIcon, ChevronUpIcon, SpeakerWaveIcon } from '@heroicons/react/24/outline';
import { listOrganizationalUnits } from '../services/api_org_unit';
import { AuthContext } from '../contexts/auth';
import { getCompanyAlerts, postAlertCompany, deleteAlertCompany, getCompanyAlertsReadbyList } from '../services/api_alerts';
import Swal from 'sweetalert2';
import Fetching from '../components/fetching';
import Empty from '../components/empty';
import { Link } from 'wouter';
import PopoverComponentCustom from '../components/popover_component';
import AlertForm from '../components/form_alert_create';
import ComunicationsList from '../components/comunications_list';
import NotificationsList from '../components/notifications_list';


export default function BoardMessage() {
  const { token, profile } = useContext(AuthContext);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isLoadingOu, setIsLoadingOu] = useState(false);
  const [selectedToggleOption, setSelectedToggleOption] = useState('Comunicazioni');
  const [isLoadingComunications, setIsLoadingComunications] = useState(true);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [isError, setIsError] = useState(false);
  const [units, setUnits] = useState([]);
  const [selectedCommunicationOption, setSelectedCommunicationOption] = useState({});
  const [comunications, setComunications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [currentPageComunications, setCurrentPageComunications] = useState(1);
  const [totalPagesComunications, setTotalPagesComunications] = useState(0);
  const [currentPageNotifications, setCurrentPageNotifications] = useState(1);
  const [totalPagesNotifications, setTotalPagesNotifications] = useState(0);
  const [titleError, setTitleError] = useState(null);
  const [messageError, setMessageError] = useState(null);
  const [recipientError, setRecipientError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filter, setFilter] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const titleRef = useRef(null);
  const messageRef = useRef(null);
  const recipientRef = useRef(null);
  const [readByData, setReadByData] = useState([]);
  const [readByVisible, setReadByVisible] = useState(false);
  const [selectedAlertId, setSelectedAlertId] = useState(null);
  const [loadingReadBy, setLoadingReadBy] = useState(false);
  const [errorLoadingReadby, setErrorLoadingReadby] = useState(false);
  const [currentPageReadby, setCurrentPageReadby] = useState(1);
  const [totalPagesReadby, setTotalPagesReadby] = useState(0);
  const [attachments, setAttachments] = useState([]);
  const [communicationSearch, setCommunicationSearch] = useState('');
  const [debouncedCommunicationSearch, setDebouncedCommunicationSearch] = useState('');
  const [communicationAttachmentFilter, setCommunicationAttachmentFilter] = useState('all');
  const [communicationPageSize, setCommunicationPageSize] = useState(25);
  const [totalComunications, setTotalComunications] = useState(0);
  const [notificationPageSize, setNotificationPageSize] = useState(25);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [showAlertForm, setShowAlertForm] = useState(false);
  const abortControllerRef = useRef(null);

  const isProfileComplete = profile.has_complete_profile;

  const canManageAlertsAll = profile.customer_type !== 'employee' || profile.employee?.permissions?.includes('hr.manage_alerts_all');

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedCommunicationSearch(communicationSearch);
    }, 400);

    return () => clearTimeout(timeout);
  }, [communicationSearch]);
  
  useEffect(() => {
    const fetchUnits = async () => {
      setIsLoadingOu(true);
      setIsError(false);

      try {
        const response = await listOrganizationalUnits(token, true, null, 'shifts');

        setUnits(response.data || [] );
      } catch (error) {
        console.error('Error fetching units:', error);
        {(error.response.status !== 402) &&
          setIsError(true);
          setIsLoadingOu(false);
        }
      } finally {
        setIsLoadingOu(false);
      }
    };

    fetchUnits();
  }, [token]);

  const fetchAlerts = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setComunications([]);
    setNotifications([]);

    if (selectedToggleOption === 'Comunicazioni') {
      setIsLoadingComunications(true);
      try {
        const hasAttachments =
          communicationAttachmentFilter === 'with'
            ? true
            : communicationAttachmentFilter === 'without'
              ? false
              : undefined;

        const comunicationsResults = await getCompanyAlerts(
          token,
          currentPageComunications,
          communicationPageSize,
          'comunication',
          'alerts',
          controller.signal,
          debouncedCommunicationSearch,
          hasAttachments
        );

        setComunications(comunicationsResults.results || []);
        setTotalComunications(comunicationsResults.count || 0);
        setTotalPagesComunications(
          Math.ceil((comunicationsResults.count || 0) / communicationPageSize)
        );
        setIsLoadingComunications(false);
      } catch (error) {
        if (error.name !== 'CanceledError') {
          setIsLoadingComunications(false);
          console.error('Errore comunicazioni:', error);
        }
      }
    }

    if (selectedToggleOption === 'Notifiche') {
      setIsLoadingNotifications(true);
      try {
        const notificationsResult = await getCompanyAlerts(
          token,
          currentPageNotifications,
          notificationPageSize,
          'notification',
          'shifts',
          controller.signal
        );

        setNotifications(notificationsResult.results || []);
        setTotalNotifications(notificationsResult.count || 0);
        setTotalPagesNotifications(
          Math.ceil(
            (notificationsResult.count || 0) / notificationPageSize
          )
        );
      } catch (error) {
        if (error.name !== 'CanceledError') {
          console.error('Errore notifiche:', error);
        }
      } finally {
        setIsLoadingNotifications(false);
      }
    }
  };

  const handleCommunicationSearchChange = (value) => {
    setCommunicationSearch(value);
    setCurrentPageComunications(1);
  };

  const handleCommunicationAttachmentFilterChange = (value) => {
    setCommunicationAttachmentFilter(value);
    setCurrentPageComunications(1);
  };

  const handleCommunicationPageSizeChange = (value) => {
    setCommunicationPageSize(value);
    setCurrentPageComunications(1);
  };

  const handleCommunicationPageChange = (page) => {
    setCurrentPageComunications(page);
  };

  const handleNotificationPageSizeChange = (value) => {
    setNotificationPageSize(value);
    setCurrentPageNotifications(1);
  };

  const handleNotificationPageChange = (page) => {
    setCurrentPageNotifications(page);
  };

  useEffect(() => {
    if (token) {
      fetchAlerts();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    token,
    selectedToggleOption,
    currentPageComunications,
    currentPageNotifications,
    communicationPageSize,
    notificationPageSize,
    communicationAttachmentFilter,
    debouncedCommunicationSearch
  ]);

  const handleNextPage = () => {
    if (selectedToggleOption === 'Comunicazioni') {
      if (currentPageComunications < totalPagesComunications) {
        setCurrentPageComunications(prevPage => prevPage + 1);
      }
    } else if (selectedToggleOption === 'Notifiche') {
      if (currentPageNotifications < totalPagesNotifications) {
        setCurrentPageNotifications(prevPage => prevPage + 1);
      }
    }
  };

  const handlePreviousPage = () => {
    if (selectedToggleOption === 'Comunicazioni') {
      if (currentPageComunications > 1) {
        setCurrentPageComunications(prevPage => prevPage - 1);
      }
    } else if (selectedToggleOption === 'Notifiche') {
      if (currentPageNotifications > 1) {
        setCurrentPageNotifications(prevPage => prevPage - 1);
      }
    }
  };

  const handleNextPageReadby = () => {
    if (currentPageReadby < totalPagesReadby) {
      setCurrentPageReadby(prevPage => prevPage + 1);
    }
  };

  const handlePreviousPageReadby = () => {
    if (currentPageReadby > 1) {
      setCurrentPageReadby(prevPage => prevPage - 1);
    }
  };


  const handleOptionChange = (option) => {
    if (selectedOption === option) {
      setSelectedOption(null);
    } else {
      setSelectedOption(option);
      if (option !== 'employee') {
        setSelectedEmployees([]);
      }
      if (option !== 'unit') {
        setSelectedUnits([]);
        setSelectedCommunicationOption({});
      }
      if (option !== 'all') {
        
      }
      setRecipientError(null);
    }
  };

  const handleCheckboxChange = (event) => {
    setFilter(event.target.value);
  };

  const handleEmployeeSelection = (employees) => {
    setSelectedEmployees(
      Array.isArray(employees) ? employees : []
    );

    setSearchQuery('');
  };
    
  const handleUnitSelection = (unit) => {
    if (selectedUnits.some((selectedUnit) => selectedUnit.id === unit.id)) {
      setSelectedUnits(selectedUnits.filter((selectedUnit) => selectedUnit.id !== unit.id));
    } else {
      setSelectedUnits([...selectedUnits, unit]);
    }
  };

  const handleCreateAlert = async (event) => {
    event.preventDefault();

    const title = event.target.elements.title.value;
    const message = event.target.elements.comment.value;

    let isValid = true;

    if (titleError) {
      setTitleError(null);
    }
    if (messageError) {
      setMessageError(null);
    }
    if (recipientError) {
      setRecipientError(null);
    }

    if (!title) {
      setTitleError('Compila il campo');
      if (titleRef.current) {
        titleRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      isValid = false;
    }

    if (!message) {
      setMessageError('Compila il campo');
      if (isValid && messageRef.current) {
        messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      isValid = false;
    }

    if (!selectedOption) {
      setRecipientError('Seleziona almeno un destinatario');
      if (isValid && recipientRef.current) {
        recipientRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      isValid = false;
    } else if (selectedOption === 'unit' && (!selectedUnits || selectedUnits.length === 0)) {
      setRecipientError('Seleziona almeno un\'unità');
      if (isValid && recipientRef.current) {
        recipientRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      isValid = false;
    } else if (selectedOption === 'employee' && (!selectedEmployees || selectedEmployees.length === 0)) {
      setRecipientError('Seleziona almeno un dipendente');
      if (isValid && recipientRef.current) {
        recipientRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    const newAlertData = {
      title,
      message,
      send_to_all: selectedOption === 'all',
      category: 'comunication',
      organizational_units: selectedOption === 'unit' ? selectedUnits.map(unit => unit.id) : [],
      employees: selectedOption === 'employee' ? selectedEmployees.map(employee => employee.id) : []
    };
    
    try {
      Swal.fire({
        title: 'Invio in corso...',
        text: 'Attendi mentre il messaggio viene inviato',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await postAlertCompany(token, newAlertData, attachments);

      setCurrentPageComunications(1);
      await fetchAlerts();

      Swal.fire({
        icon: 'success',
        title: 'Messaggio Inviato!',
        text: 'Il messaggio è stato inviato correttamente',
        timer: 2500,
        showConfirmButton: false,
      });

      event.target.reset();
      setSelectedOption(null);
      setSelectedUnits([]);
      setSelectedEmployees([]);
      setSelectedCommunicationOption({});
      setAttachments([]);
      setShowAlertForm(false);
    } catch (error) {
      console.error('Errore durante l\'invio del messaggio:', error);
      let errorMessage = 'Non è stato possibile inviare il messaggio. Per favore riprova.';
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        const errorMessages = [];
  
        Object.keys(errorData).forEach((key) => {
          const messageArray = errorData[key];
          if (Array.isArray(messageArray)) {
            errorMessages.push(...messageArray);
          } else if (typeof messageArray === 'string') {
            errorMessages.push(messageArray);
          }
        });
  
        if (errorMessages.length > 0) {
          errorMessage = errorMessages.join(' ');
        }
      }
  
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: errorMessage,
        confirmButtonText: 'OK',
      });
    }
  };

  const handleShowDetails = (messageId) => {
    setShowDetails((prevDetailsVisibility) => ({
      ...prevDetailsVisibility,
      [messageId]: !prevDetailsVisibility[messageId]
    }));
  };

  const handleToggleChange = (option) => {
    setSelectedToggleOption(option);

    if (option === 'Comunicazioni') {
      setCurrentPageComunications(1);
      setIsLoadingComunications(true);
    } else {
      setCurrentPageNotifications(1);
      setIsLoadingNotifications(true);
    }
  };

  const handleDeleteAlert = async (id, title) => {
    Swal.fire({
      icon: "warning",
      title: 'Conferma eliminazione',
      text: `Stai eliminando l'avviso "${title}". Sei sicuro di voler procedere?`,
      showCancelButton: true,
      confirmButtonText: "Sì",
      cancelButtonText: "Annulla",
      cancelButtonColor: "#d1d5db",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteAlertCompany(token, id);

          if (comunications.length === 1 && currentPageComunications > 1) {
            setCurrentPageComunications(prev => prev - 1);
          } else {
            await fetchAlerts();
          }
  
          Swal.fire({
            position: "center",
            icon: "success",
            title: "Avviso eliminato con successo!",
            showConfirmButton: false,
            timer: 1500
          });
        } catch (error) {
          console.error('Errore durante l\'eliminazione:', error);
          let errorMessage = 'Non è stato possibile eliminare l\'avviso. Per favore riprova.';
          if (error.response && error.response.data) {
            const errorData = error.response.data;
            const errorMessages = [];
      
            Object.keys(errorData).forEach((key) => {
              const messageArray = errorData[key];
              if (Array.isArray(messageArray)) {
                errorMessages.push(...messageArray);
              } else if (typeof messageArray === 'string') {
                errorMessages.push(messageArray);
              }
            });
      
            if (errorMessages.length > 0) {
              errorMessage = errorMessages.join(' ');
            }
          }
      
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: errorMessage,
            confirmButtonText: 'OK',
          });
        }
      };
    });
  };

  const handleToggleReadBy = (alertId) => {
    if (readByVisible && selectedAlertId === alertId) {
      setReadByVisible(false);
    } else {
      setSelectedAlertId(alertId);
      setCurrentPageReadby(1);
      setReadByVisible(true);
    }
  };

  useEffect(() => {
    const fetchReadByData = async () => {
      if (readByVisible && selectedAlertId) {
        try {
          setLoadingReadBy(true);
          setErrorLoadingReadby(false);
          const perPage = 20;
          const res = await getCompanyAlertsReadbyList(token, selectedAlertId, currentPageReadby, perPage, 'alerts');
          setReadByData(res.results || []);
          setTotalPagesReadby(Math.ceil(res.count / perPage));
        } catch (error) {
          console.error("Errore nel recupero dei dati:", error);
          setErrorLoadingReadby("Errore nel recupero dei dati.");
        } finally {
          setLoadingReadBy(false);
        }
      }
    };

    fetchReadByData();
  }, [currentPageReadby, readByVisible, selectedAlertId]);

  if (isError) return <div>Error loading data</div>;
 
  return (
    <div className="flex flex-col min-h-screen">
      <PopoverComponentCustom title='Bacheca Avvisi' />
      {profile?.customer_type === 'company' && !isProfileComplete && profile?.company?.email == profile?.the_real_user_email && (
        <div className="w-full">
          <div className="bg-orange-100 text-orange-600 p-4 mb-6">
            <p className="text-center text-sm font-semibold">
              Il profilo non è completo. <br />
              Compila i campi mancanti{' '}
              <Link
                to="/profilo/fatturazione"
                className="text-indigo-600 hover:text-indigo-800 underline"
              >
                cliccando qui
              </Link>
            </p>
          </div>
        </div>
      )}
      <main className="flex-grow mx-auto w-full max-w-[1400px] px-4 py-8">
        <div className="mb-6 overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
          <button
            type="button"
            onClick={() => setShowAlertForm(prev => !prev)}
            className={`flex w-full items-center justify-between gap-4 p-4 text-left transition-colors sm:p-5 ${
              showAlertForm
                ? 'border-b border-gray-200 bg-indigo-50'
                : 'hover:bg-gray-50'
            }`}
          >
            <div className="flex min-w-0 items-center gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
                <PencilSquareIcon className="h-6 w-6" />
              </div>

              <div className="min-w-0">
                <h3 className="text-sm font-semibold uppercase text-indigo-800 sm:text-base">
                  Nuovo messaggio
                </h3>

                <p className="mt-1 text-xs text-gray-500">
                  Crea e invia una nuova comunicazione ai dipendenti
                </p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <span className="hidden text-xs font-semibold text-gray-500 sm:block">
                {showAlertForm ? 'Chiudi' : 'Scrivi messaggio'}
              </span>

              <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500">
                {showAlertForm ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4" />
                )}
              </div>
            </div>
          </button>

          {showAlertForm && (
            <div className="p-4 sm:p-5">
              <AlertForm
                titleError={titleError}
                setTitleError={setTitleError}
                messageError={messageError}
                setMessageError={setMessageError}
                recipientError={recipientError}
                selectedOption={selectedOption}
                selectedUnits={selectedUnits}
                selectedEmployees={selectedEmployees}
                units={units}
                filter={filter}
                searchQuery={searchQuery}
                isLoadingOu={isLoadingOu}
                handleOptionChange={handleOptionChange}
                handleUnitSelection={handleUnitSelection}
                handleEmployeeSelection={handleEmployeeSelection}
                handleCheckboxChange={handleCheckboxChange}
                handleCreateAlert={handleCreateAlert}
                token={token}
                titleRef={titleRef}
                messageRef={messageRef}
                recipientRef={recipientRef}
                attachments={attachments}
                setAttachments={setAttachments}
                canManageAlertsAll={canManageAlertsAll}
              />
            </div>
          )}
        </div>

  
        <div className="mt-4">
          <div className="flex flex-col rounded-t-xl border-t border-x border-gray-300 bg-white p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
                  <SpeakerWaveIcon className="h-6 w-6" />
                </div>

                <div className="min-w-0">
                  <h3 className="text-base font-semibold uppercase text-indigo-800">
                    Bacheca avvisi
                  </h3>

                  <p className="mt-1 text-xs text-gray-500">
                    Visualizza le comunicazioni inviate ai dipendenti o le notifiche generate dal sistema
                  </p>
                </div>
              </div>
            </div>

            <div className="grid w-full grid-cols-2 overflow-hidden rounded-lg shadow-sm lg:w-auto lg:min-w-[440px]">
              {[
                {
                  key: 'Comunicazioni',
                  label: 'Comunicazioni',
                  Icon: EnvelopeIcon,
                },
                {
                  key: 'Notifiche',
                  label: 'Notifiche',
                  Icon: BellIcon,
                },
              ].map(({ key, label, Icon }, index) => {
                const active = selectedToggleOption === key;

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleToggleChange(key)}
                    className={`relative flex min-w-0 items-center justify-center gap-2 border px-4 py-3 text-xs font-semibold transition sm:text-sm
                      ${index === 0 ? 'rounded-l-lg' : 'rounded-r-lg'}
                      ${
                        active
                          ? 'z-10 border-indigo-300 bg-indigo-100 text-indigo-800'
                          : 'border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100'
                      }
                    `}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    <span className="truncate">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {selectedToggleOption === 'Comunicazioni' ? (
          isLoadingComunications && comunications.length === 0 && !communicationSearch ? (
            <Fetching />
          ) : (
            <ComunicationsList
              messages={comunications}
              showDetails={showDetails}
              handleShowDetails={handleShowDetails}
              handleDeleteAlert={handleDeleteAlert}
              handleToggleReadBy={handleToggleReadBy}
              readByVisible={readByVisible}
              selectedAlertId={selectedAlertId}
              readByData={readByData}
              loadingReadBy={loadingReadBy}
              errorLoadingReadby={errorLoadingReadby}
              currentPageReadby={currentPageReadby}
              totalPagesReadby={totalPagesReadby}
              handleNextPageReadby={handleNextPageReadby}
              handlePreviousPageReadby={handlePreviousPageReadby}
              currentPage={currentPageComunications}
              totalPages={totalPagesComunications}
              totalCount={totalComunications}
              searchQuery={communicationSearch}
              handleSearchChange={handleCommunicationSearchChange}
              attachmentFilter={communicationAttachmentFilter}
              handleAttachmentFilterChange={handleCommunicationAttachmentFilterChange}
              pageSize={communicationPageSize}
              handlePageSizeChange={handleCommunicationPageSizeChange}
              handlePageChange={handleCommunicationPageChange}
            />
          )
        ) : isLoadingNotifications ? (
          <Fetching />
        ) : notifications.length === 0 ? (
          <Empty />
        ) : (
          <NotificationsList
            notifications={notifications}
            currentPage={currentPageNotifications}
            totalPages={totalPagesNotifications}
            totalCount={totalNotifications}
            pageSize={notificationPageSize}
            handlePageSizeChange={handleNotificationPageSizeChange}
            handlePageChange={handleNotificationPageChange}
          />
        )}
      
      </main>
      <Footer />
    </div>
  );
}
